import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, DEFAULT_CATEGORY_NAME } from '../../api/db';
import RowEditor from '../../components/admin/RowEditor';
import MediaPicker from '../../components/admin/MediaPicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { parsePastedContent } from '../../utils/pasteParser';

const { FiSave, FiArrowLeft, FiColumns, FiSquare, FiExternalLink, FiClipboard } = FiIcons;

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    summary: '',
    cover_image: '',
    category_id: '',
    status: 'draft',
    content: []
  });
  const [categories, setCategories] = useState([]);
  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [pasteContent, setPasteContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const cats = await db.getCategories();
      setCategories(cats);
      if (id && id !== 'new') {
        const article = await db.getArticleById(id);
        if (article) setFormData(article);
      } else {
        const education = await db.getCategoryBySlug('education');
        setFormData(prev => ({
          ...prev,
          _isNew: true,
          category_id: education?.id || 'cat-education'
        }));
      }
    };
    load();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const data = { ...prev, [name]: value };
      // Auto-generate slug from title if title is changed and slug is empty or matches old title
      if (name === 'title' && (!prev.slug || prev.slug === prev.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''))) {
        data.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
      }
      return data;
    });
  };

  const handleSelectCoverImage = (url) => {
    setFormData(prev => ({ ...prev, cover_image: url }));
    setIsMediaPickerOpen(false);
  };

  const addRow = (columns) => {
    const newRow = {
      id: `row-${Date.now()}`,
      type: 'row',
      columns,
      elements: []
    };
    setFormData(prev => ({
      ...prev,
      content: [...prev.content, newRow]
    }));
  };

  const updateRow = (rowId, updates) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content.map(row => row.id === rowId ? { ...row, ...updates } : row)
    }));
  };

  const removeRow = (rowId) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content.filter(row => row.id !== rowId)
    }));
  };

  const handlePasteContent = () => {
    if (!pasteContent.trim()) return;
    const blocks = parsePastedContent(pasteContent);
    if (!blocks.length) return;

    const newRow = {
      id: `row-${Date.now()}`,
      type: 'row',
      columns: 1,
      elements: blocks.map((block, idx) => ({
        id: `el-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 9)}`,
        col: 0,
        ...block
      }))
    };

    setFormData(prev => ({
      ...prev,
      content: [...prev.content, newRow]
    }));
    setPasteContent('');
    setIsPasteModalOpen(false);
  };

  const handleSave = async (status) => {
    if (!formData.title || !formData.slug) {
      alert('Title and Slug are required.');
      return;
    }
    setSaving(true);
    try {
      await db.saveArticle({ ...formData, status });
      navigate('/admin');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Actions */}
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200">
        <button 
          onClick={() => navigate('/admin')}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-2 font-medium"
        >
          <SafeIcon icon={FiArrowLeft} /> Back to Dashboard
        </button>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button 
            onClick={() => handleSave('published')}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Metadata Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8 space-y-6">
        <h2 className="text-xl font-bold text-gray-900 border-b pb-2">Article Metadata</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input 
                type="text" 
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter article title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
              <input 
                type="text" 
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                placeholder="url-friendly-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image Path/URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="cover_image"
                  value={formData.cover_image}
                  onChange={handleChange}
                  className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="/assets/image.png or URL"
                />
                <button
                  type="button"
                  onClick={() => setIsMediaPickerOpen(true)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Browse
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category_id"
                value={formData.category_id || ''}
                onChange={handleChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{DEFAULT_CATEGORY_NAME} (default)</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
            <textarea 
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              rows={6}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Brief description for the article card..."
            />
          </div>
        </div>
      </div>

      {/* Builder Section */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Content Builder</h2>
          <a
            href="https://haringeylearns.imgbb.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
            style={{ color: 'var(--brand-primary)' }}
          >
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            Image Library
          </a>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsPasteModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border"
            style={{ backgroundColor: 'var(--brand-primary-light)', borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}
          >
            <SafeIcon icon={FiClipboard} /> Paste Content
          </button>
          <button
            onClick={() => addRow(1)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 text-gray-700"
          >
            <SafeIcon icon={FiSquare} /> Add 1-Col Row
          </button>
          <button
            onClick={() => addRow(2)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 text-gray-700"
          >
            <SafeIcon icon={FiColumns} /> Add 2-Col Row
          </button>
        </div>
      </div>

      <div className="space-y-2 pb-20">
        {formData.content.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
            <p className="text-gray-500 mb-4">Start building your article by adding a row or pasting content.</p>
            <div className="flex justify-center gap-4">
              <button onClick={() => setIsPasteModalOpen(true)} className="font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>Paste from Word / Markdown</button>
              <button onClick={() => addRow(1)} className="font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>Add Full Width Row</button>
              <button onClick={() => addRow(2)} className="font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>Add Split Row</button>
            </div>
          </div>
        ) : (
          formData.content.map((row) => (
            <RowEditor
              key={row.id}
              row={row}
              updateRow={updateRow}
              removeRow={removeRow}
            />
          ))
        )}
      </div>

      {isMediaPickerOpen && (
        <MediaPicker
          onSelect={handleSelectCoverImage}
          onClose={() => setIsMediaPickerOpen(false)}
        />
      )}

      {isPasteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Paste Content</h3>
              <p className="text-sm text-gray-500 mt-1">
                Paste content from Word, Google Docs, or Markdown. Text, headings, bullet lists, and links will be converted automatically. YouTube and Canva URLs become embed blocks. Images become placeholders.
              </p>
            </div>
            <div className="p-6 flex-1 overflow-hidden flex flex-col">
              <textarea
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                placeholder="Paste your content here..."
                rows={12}
                className="w-full flex-1 border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 p-3 resize-none"
                autoFocus
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setPasteContent('');
                  setIsPasteModalOpen(false);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePasteContent}
                className="px-4 py-2 text-white rounded-lg font-medium"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                Append to Article
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleEditor;